/**
 * ConsolidationTable component displays a table with taxon data and allows for editing and filtering.
 *
 * @param {Object} props - The properties object.
 * @param {Array} props.rows - The rows of the table.
 * @param {Function} props.setRows - Function to update the rows of the table.
 * @param {Object} props.fileData - Data related to the file being processed.
 * @param {string} props.refVersion - The reference version for the taxon data.
 * @param {Function} props.searchTaxaName - Function to search for taxon names.
 * @param {Object} props.responseTaxaName - Response object containing taxon names.
 * @param {Function} props.handleValidName - Function to handle the validation of taxon names.
 * @param {Object} props.responseValidName - Response object containing valid taxon names.
 * @param {Object} props.apiRef - Reference to the DataGrid API.
 * @param {Object} props.columnVisibilityModel - Model for column visibility in the DataGrid.
 * @param {Object} props.filterModel - Model for filtering in the DataGrid.
 * @param {Function} props.handleSwitchChange - Function to handle changes in the filter switches.
 * @param {string} props.activeSwitch - The currently active switch for filtering.
 *
 * @returns {JSX.Element} The rendered ConsolidationTable component.
 */

import { Box, FormControlLabel, Icon, Switch } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import {
  DataGrid,
  GridCellModes,
  GridCellModesModel,
  GridCellParams,
  GridColDef,
} from "@mui/x-data-grid";
import { useCallback, useEffect, useState } from "react";
import AutocompleteSearch from "@front-ends/common/src/components/app/AutocompleteSearch";

export default function ConsolidationTable({
  // Data pour le tableau
  rows,
  setRows,
  fileData,
  // Version de référence
  referentiel,
  refVersion,
  filterType,
  // Appels API des référentiels
  searchTaxaName,
  //isFinishedValidName,
  responseTaxaName,
  handleValidName,
  responseValidName,
  // Filtres et API de Material-UI
  apiRef,
  columnVisibilityModel,
  filterModel,
  handleSwitchChange,
  activeSwitch,
  // Label du tableau
  quotedNameLabel,
  consolidatedNameLabel,
  validNameLabel,
  taxonFoundLabel,
  taxonAlmostFoundLabel,
  taxonNotFoundLabel,
  vernacularNameLabel,
  synonymyLabel,
  taxonGroupLabel,
  vernacularNameFilterLabel,
  synonymyFilterLabel,
  taxonGroupFilterLabel,
  yesLabel,
  noLabel,
}: any) {
  // Variables pour l'autocompletion du tableau
  const [inputValue, setInputValue] = useState("");
  const taxaResponse = responseTaxaName?.taxons ?? [];
  var rowId: any;
  const [rowIndex, setRowIndex] = useState(null);

  // Variables pour la gestion des colonnes supplementaires
  const [isVernacularNameAdded, setIsVernacularNameAdded] = useState(false);
  const [isSynonymyAdded, setIsSynonymyAdded] = useState(false);
  const [taxonGroupFilter, setTaxonGroupFilter] = useState(false);

  // Initialise lignes du tableau
  useEffect(() => {
    setRows((rows: any[]) =>
      rows.map((row) => ({
        ...row,
        synonymy: row.rightValueName === row.validName ? noLabel : yesLabel,
        txgroup: row.taxonGroupName || "",
      }))
    );
  }, []);

  // Met à jour les lignes du tableau lorsque les données changent
  useEffect(() => {
    if (responseValidName && rowIndex !== null) {
      setRows((rows: any[]) =>
        rows.map((row, index) =>
          index === rowIndex
            ? {
                ...row,
                validName: responseValidName?.taxon.name,
                validKey: responseValidName?.taxon.key,
                synonymy:
                  row.rightValueName === responseValidName?.taxon.name
                    ? noLabel
                    : yesLabel,
                txgroup: responseValidName?.taxon.txGroup.name,
              }
            : row
        )
      );
    }
  }, [responseValidName]);

  // Gérer les noms consolidés
  function handleConsolidatedName(newValue: any) {
    // functions filters
    updateEditableColumn(newValue);
    updateHiddenColumn(newValue);
    // function valid name
    setRowIndex(rowId);
    handleValidName(newValue.ref);
  }

  // Met à jour la colone éditable qui va chercher le taxon consolidé avec la nouvelle valeur choisie
  function updateEditableColumn(newValue: any) {
    setRows((rows: any[]) =>
      rows.map((row, index) =>
        index === rowId
          ? {
              ...row,
              rightValueName: newValue.name,
              rightValueKey: newValue.key,
              vernacularName:
                Array.isArray(newValue.vernacularName) &&
                newValue.vernacularName !== null
                  ? newValue.vernacularName[0] || ""
                  : "",
              synonymy: newValue.key != newValue.ref ? yesLabel : noLabel,
              txgroup: newValue.taxonGroupName || "",
            }
          : row
      )
    );
  }
   // Met à jour les colonnes cachées qui indiquent si le taxon a été correctement trouvé pour les filtres
  function updateHiddenColumn(newValue: any) {
    const isTaxonFound = newValue.name === rows[rowId].leftValueName;
    const isTaxonAlmostFound =
      newValue.name.toLowerCase().includes(inputValue.toLowerCase()) &&
      !isTaxonFound;
    setRows((rows: any[]) =>
      rows.map((row, index) =>
        index === rowId ? { ...row, isTaxonFound, isTaxonAlmostFound } : row
      )
    );
  }

  // Recherche des noms de taxons dans autocomplete
  function customSearchItems(_event: React.SyntheticEvent, value: string) {
    if (value !== "" && value.length > 2) {
      searchTaxaName(value, refVersion, referentiel, filterType).then(() => {
        {
          setInputValue && setInputValue(value);
        }
      });
    }
  }

  // Gérer le changement de mode de cellule
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});

  /**
   * @IA_Generated
   */
  const handleCellClick = useCallback(
    (params: GridCellParams, event: React.MouseEvent) => {
      if (!params.isEditable) {
        return;
      }

      if (
        (event.target as any).nodeType === 1 &&
        !event.currentTarget.contains(event.target as Element)
      ) {
        return;
      }

      // Met à jour l'état des champs pour chaques lignes du tableau de consolidation.
      setCellModesModel((prevModel) => {
        return {
          // on itère sur chaque ligne du tableau
          ...Object.keys(prevModel).reduce(
            (item, id) => ({
              ...item,
              //puis on itère sur chaque champs de la ligne
              [id]: Object.keys(prevModel[id]).reduce(
                (item2, field) => ({
                  ...item2,
                  //on met met chaque champs en mode vue donc pas éditables
                  [field]: { mode: GridCellModes.View },
                }),
                {}
              ),
            }),
            {}
          ),
          // Met uniquement les cellules du champs de consolidation en mode édition
          [params.id]: {
            ...Object.keys(prevModel[params.id] || {}).reduce(
              (item, field) => ({
                ...item,
                [field]: { mode: GridCellModes.View },
              }),
              {}
            ),
            [params.field]: { mode: GridCellModes.Edit },
          },
        };
      });
    },
    []
  );

  // Gère la mise à jour des cellules pour chaques lignes en fonction du changement de valeur du taxon consolidé
  const handleCellModesModelChange = useCallback(
    (newModel: GridCellModesModel) => {
      setCellModesModel(newModel);
    },
    []
  );

  const columns: GridColDef[] = [
    {
      field: "leftValueName",
      headerName: "Nom cité",
      renderHeader: () => (
        <div
          style={{
            fontWeight: "bold",
          }}
        >
          {quotedNameLabel}
        </div>
      ),
      flex: 400,
      align: "left",
      headerAlign: "left",
      disableColumnMenu: true,
    },
    {
      field: "rightValueName",
      headerName: "Nom consolidé",
      renderHeader: () => (
        <div
          style={{
            fontWeight: "bold",
          }}
        >
          {consolidatedNameLabel}
        </div>
      ),
      renderEditCell: (params) => {
        rowId = Number(params.id) - 1;
        return (
          <AutocompleteSearch
            optionsLabel="name"
            optionsName=""
            keyLabel="key"
            searchStore={searchTaxaName}
            responseStore={taxaResponse}
            addResultFunction={handleConsolidatedName}
            displayLabel=""
            customSearchItems={customSearchItems}
            multipleResponse={false}
          />
        );
      },
      renderCell: (params) => {
        return (
          <div>
            {params.value === null ? (
              <Icon color="error" sx={{ mr: 0.5 }}>
                <CancelIcon fontSize="small" sx={{ mb: 1 }} />
              </Icon>
            ) : params.value === params.row.leftValueName ? (
              <Icon color="primary" sx={{ mr: 0.5 }}>
                <CheckCircleIcon fontSize="small" sx={{ mb: 1 }} />
              </Icon>
            ) : (
              <Icon color="warning" sx={{ mr: 0.5 }}>
                <WarningIcon fontSize="small" sx={{ mb: 1 }} />
              </Icon>
            )}
            {params.value && params.value !== "" ? (
              <span>{params.value}</span>
            ) : (
              <>
                <Icon sx={{ mr: 0.5 }}>
                  <SearchIcon fontSize="small" sx={{ mb: 1 }} />
                </Icon>
              </>
            )}
          </div>
        );
      },
      flex: 400,
      align: "left",
      headerAlign: "left",
      disableColumnMenu: true,
      editable: true,
    },
    {
      field: "vernacularName",
      headerName: "Nom vernaculaire",
      renderHeader: () => (
        <div
          style={{
            fontWeight: "bold",
          }}
        >
          {vernacularNameLabel}
        </div>
      ),
      flex: 400,
      align: "left",
      headerAlign: "left",
      disableColumnMenu: true,
      disableExport: !isVernacularNameAdded,
    },
    {
      field: "validName",
      headerName: "Nom valide",
      renderHeader: () => (
        <div
          style={{
            fontWeight: "bold",
          }}
        >
          {validNameLabel}
        </div>
      ),
      flex: 400,
      align: "left",
      headerAlign: "left",
      disableColumnMenu: true,
    },
    {
      field: "synonymy",
      headerName: "Synonymie",
      renderHeader: () => (
        <div
          style={{
            fontWeight: "bold",
          }}
        >
          {synonymyLabel}
        </div>
      ),
      flex: 200,
      align: "left",
      headerAlign: "left",
      disableColumnMenu: true,
      disableExport: !isSynonymyAdded,
      renderCell: (params) => {
        return params.value === yesLabel ? yesLabel : noLabel;
      },
    },
    {
      field: "txgroup",
      headerName: "Groupe taxonomique",
      renderHeader: () => (
        <div
          style={{
            fontWeight: "bold",
          }}
        >
          {taxonGroupLabel}
        </div>
      ),
      flex: 200,
      align: "left",
      headerAlign: "left",
      disableColumnMenu: true,
      disableExport: !taxonGroupFilter,
    },
    {
      field: "isTaxonFound",
      headerName: "Taxon Found",
      renderHeader: () => "Taxon Found",
      disableExport: true,
    },
    {
      field: "isTaxonAlmostFound",
      headerName: "Taxon Almost Found",
      renderHeader: () => "Taxon Almost Found",
      disableExport: true,
    },
  ];

  return (
    <>
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={activeSwitch === "isTaxonFound"}
              onChange={() =>
                handleSwitchChange("isTaxonFound", {
                  field: "isTaxonFound",
                  operator: "equals",
                  value: "true",
                })
              }
            />
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <CheckCircleIcon color="primary" sx={{ mr: 0.5 }} />
              {taxonFoundLabel}
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Switch
              checked={activeSwitch === "isTaxonAlmostFound"}
              onChange={() =>
                handleSwitchChange("isTaxonAlmostFound", {
                  field: "isTaxonAlmostFound",
                  operator: "equals",
                  value: "true",
                })
              }
            />
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <WarningIcon color="warning" sx={{ mr: 0.5 }} />
              {taxonAlmostFoundLabel}
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Switch
              checked={activeSwitch === "rightValueName"}
              onChange={() =>
                handleSwitchChange("rightValueName", {
                  field: "rightValueName",
                  operator: "isEmpty",
                })
              }
            />
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <CancelIcon color="error" sx={{ mr: 0.5 }} />
              {taxonNotFoundLabel}
            </Box>
          }
        />
      </Box>
      <DataGrid
        disableRowSelectionOnClick
        rows={rows}
        columns={columns}
        cellModesModel={cellModesModel}
        onCellModesModelChange={handleCellModesModelChange}
        onCellClick={handleCellClick}
        processRowUpdate={(_oldRow, newRow) => {
          fileData.consolidateReport.values[newRow.id] = newRow;
          return newRow;
        }}
        columnVisibilityModel={{
          ...columnVisibilityModel,
          vernacularName: isVernacularNameAdded,
          synonymy: isSynonymyAdded,
          txgroup: taxonGroupFilter,
        }}
        filterModel={filterModel}
        apiRef={apiRef}
      />
      {/* Add additional columns*/}
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={isVernacularNameAdded}
                onChange={(e) => {
                  setIsVernacularNameAdded(e.target.checked);
                }}
              />
            }
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {vernacularNameFilterLabel}
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={isSynonymyAdded}
                onChange={(e) => {
                  setIsSynonymyAdded(e.target.checked);
                }}
              />
            }
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {synonymyFilterLabel}
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={taxonGroupFilter}
                onChange={(e) => {
                  setTaxonGroupFilter(e.target.checked);
                }}
              />
            }
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {taxonGroupFilterLabel}
              </Box>
            }
          />
        </Box>
    </>
  );
}
